import { SESSION_ENDED_CODE, isRefusedCredential } from '@/model/client/authFailure';
import {
  isSessionDead,
  refreshAccessToken,
  reportRefreshedTokenRejected,
} from '@/model/client/tokenRefresh';
import { API_URL } from '@/shared/config/env';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

type TokenState = { readonly userData: { readonly apiToken: string | null } };

const SESSION_ENDED: FetchBaseQueryError = {
  status: 401,
  data: { error: { code: SESSION_ENDED_CODE, message: 'The session ended; sign in again' } },
};

export { API_URL };

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as TokenState;
    const token = state.userData.apiToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  if (isSessionDead()) return { error: SESSION_ENDED };

  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401 || result.error?.status === 403) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      const retryArgs =
        typeof args === 'string'
          ? { url: args, headers: { Authorization: `Bearer ${newToken}` } }
          : {
              ...args,
              headers: {
                ...((args as FetchArgs).headers ?? {}),
                Authorization: `Bearer ${newToken}`,
              },
            };

      const retried = await rawBaseQuery(retryArgs, api, extraOptions);
      if (isRefusedCredential(retried.error?.status, retried.error?.data)) {
        reportRefreshedTokenRejected();
      }

      return retried;
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [
    'admin',
    'agent',
    'alerts',
    'analysis-specs',
    'apikeys',
    'artifacts',
    'audit',
    'auth',
    'billing',
    'controls',
    'credits',
    'dashboards',
    'data-egress',
    'data-shares',
    'diagnostic',
    'drag_and_drop',
    'entitlements',
    'etl',
    'etl-refresh',
    'etl-sources',
    'etl-tasks',
    'mcp',
    'memory',
    'mining',
    'observability',
    'permissions',
    'private-connections',
    'project-knowledge',
    'project-members',
    'projects',
    'quota',
    'reports',
    'salesforce',
    'schedules',
    'schedules-internal',
    'secrets',
    'shade',
    'source-states',
    'storage',
    'studio',
    'support',
    'tables',
    'tiers',
    'users',
    'workspace-knowledge',
    'workspaces',
  ],
  endpoints: () => ({}),
});
